"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Serializer_1 = require("./Serializer");
const types_1 = require("../schema/types");
const __1 = require("..");
const NamespacedNamed_1 = require("../NamespacedNamed");
const result_1 = require("../result");
class DowncastSerializer extends Serializer_1.BaseSerializer {
    canSerialize(value) {
        return true;
    }
    serializeWithContext(value) {
        return value;
    }
    deserializeWithContext(value) {
        return result_1.deserializationValue(value);
    }
    getType(typeSystem) {
        return new types_1.AnyType();
    }
}
exports.DowncastSerializer = DowncastSerializer;
class LiteralSerializer extends Serializer_1.BaseSerializer {
    constructor(value) {
        super();
        this.value = value;
    }
    canSerialize(value) {
        return value === this.value;
    }
    serializeWithContext(value) {
        return value;
    }
    deserializeWithContext(value) {
        if (value !== this.value) {
            return result_1.deserializationError({
                message: `Expected "${this.value}" but got "${value}".`,
            });
        }
        return result_1.deserializationValue(value);
    }
    getType(typeSystem) {
        return new types_1.LiteralType(this.value);
    }
}
exports.LiteralSerializer = LiteralSerializer;
function sLiteral(literal) {
    return new LiteralSerializer(literal);
}
exports.sLiteral = sLiteral;
class TypeSerializer extends Serializer_1.BaseSerializer {
    constructor(typeName) {
        super();
        this.typeName = typeName;
    }
    canSerialize(value) {
        return typeof value === this.typeName;
    }
    deserializeWithContext(value) {
        if (!this.canSerialize(value)) {
            return result_1.deserializationError({
                message: `Expected a ${this.typeName} but got a ${typeof value}.`,
            });
        }
        return result_1.deserializationValue(value);
    }
    serializeWithContext(value) {
        return value;
    }
    getType(typeSystem) {
        if (this.typeName === "boolean") {
            return new types_1.BooleanType();
        }
        else if (this.typeName === "number") {
            return new types_1.NumberType();
        }
        else if (this.typeName === "string") {
            return new types_1.StringType();
        }
        else {
            throw new Error();
        }
    }
}
exports.TypeSerializer = TypeSerializer;
exports.sString = new TypeSerializer("string");
exports.sBoolean = new TypeSerializer("boolean");
exports.sNumber = new TypeSerializer("number");
class MapSerializer extends Serializer_1.BaseSerializer {
    constructor(itemSerializer) {
        super();
        this.itemSerializer = itemSerializer;
    }
    canSerialize(value) {
        if (typeof value !== "object" || value === null) {
            return false;
        }
        // TODO
        return true;
    }
    serializeWithContext(value, context) {
        const result = {};
        for (const [key, val] of Object.entries(value)) {
            result[key] = this.itemSerializer.serializeWithContext(val, context);
        }
        return result;
    }
    deserializeWithContext(value, context) {
        if (typeof value !== "object" || value === null) {
            return result_1.deserializationError({
                message: `Expected an object, but got ${typeof value}.`,
            });
        }
        const result = {};
        for (const [key, val] of Object.entries(value)) {
            const r = this.itemSerializer.deserializeWithContext(val, context);
            if (!r.isOk) {
                return result_1.deserializationError(...r.errors.map(e => e.prependPath(key)));
            }
            result[key] = r.value;
        }
        return result_1.deserializationValue(result);
    }
    getType(typeSystem) {
        return new types_1.MapType(this.itemSerializer.getType(typeSystem));
    }
}
exports.MapSerializer = MapSerializer;
function sMap(itemSerializer) {
    return new MapSerializer(itemSerializer);
}
exports.sMap = sMap;
function sRef(serializerRef) {
    return new RefSerializer(serializerRef);
}
exports.sRef = sRef;
class RefSerializer extends Serializer_1.DelegatingSerializer {
    constructor(serializerRef) {
        super();
        this.serializerRef = serializerRef;
        this.cached = undefined;
    }
    get underlyingSerializer() {
        if (!this.cached) {
            this.cached = this.serializerRef();
        }
        return this.cached;
    }
    getType(typeSystem) {
        return this.underlyingSerializer.getType(typeSystem);
    }
}
exports.RefSerializer = RefSerializer;
function sArray(itemSerializer) {
    return new ArraySerializer(itemSerializer);
}
exports.sArray = sArray;
class ArraySerializer extends Serializer_1.BaseSerializer {
    constructor(itemSerializer) {
        super();
        this.itemSerializer = itemSerializer;
    }
    canSerialize(value) {
        return value instanceof Array;
    }
    serializeWithContext(value, context) {
        return value.map(v => this.itemSerializer.serializeWithContext(v, context));
    }
    deserializeWithContext(value, context) {
        if (!(value instanceof Array)) {
            return result_1.deserializationError({
                message: `Expected an array but got a ${typeof value}.`,
            });
        }
        const errors = new Array();
        const result = new Array(value.length);
        for (let i = 0; i < value.length; i++) {
            const r = this.itemSerializer.deserializeWithContext(value[i], context);
            if (!r.isOk) {
                errors.push(...r.errors.map(e => e.prependPath(i)));
            }
            else {
                result[i] = r.value;
            }
        }
        if (errors.length > 0) {
            return result_1.deserializationError(...errors);
        }
        return result_1.deserializationValue(result);
    }
    getType(typeSystem) {
        return new types_1.ArrayType(this.itemSerializer.getType(typeSystem));
    }
}
exports.ArraySerializer = ArraySerializer;
class UnionSerializer extends Serializer_1.BaseSerializer {
    constructor(serializers) {
        super();
        this.serializers = serializers;
    }
    canSerialize(value) {
        return this.serializers.some(s => s.canSerialize(value));
    }
    serializeWithContext(value, context) {
        for (const s of this.serializers) {
            if (s.canSerialize(value)) {
                return s.serializeWithContext(value, context);
            }
        }
        throw new Error("No serializer could deserialize the given value");
    }
    deserializeWithContext(value, context) {
        const results = new Array();
        for (const s of this.serializers) {
            const result = s.deserializeWithContext(value, context);
            if (result.isOk) {
                return result;
            }
            else {
                results.push(result);
            }
        }
        throw new Error("No serializer could deserialize the given value");
    }
    getType(typeSystem) {
        return new types_1.UnionType(this.serializers.map(s => s.getType(typeSystem)));
    }
}
exports.UnionSerializer = UnionSerializer;
function sUnion(...serializers) {
    return new UnionSerializer(serializers);
}
exports.sUnion = sUnion;
class NamespacedNameSerializer extends Serializer_1.BaseSerializer {
    canSerialize(value) {
        return value instanceof __1.NamespacedName;
    }
    serializeWithContext(value, context) {
        const prefix = context.getPrefixForNamespace(NamespacedNamed_1.namespace(value.namespace));
        return `${prefix}#${value.name}`;
    }
    deserializeWithContext(value, context) {
        if (typeof value !== "string") {
            return result_1.deserializationError({ message: "must be of type string" });
        }
        const regExp = /(.*)#(.*)/;
        const m = regExp.exec(value);
        if (m) {
            const nsPrefix = m[1];
            const name = m[2];
            const ns = context.lookupNamespace(nsPrefix);
            return result_1.deserializationValue(ns(name));
        }
        else {
            throw new Error(`Malformed type "${value}"`);
        }
    }
    getType(typeSystem) {
        return new types_1.StringType();
    }
}
exports.NamespacedNameSerializer = NamespacedNameSerializer;
exports.sNamespacedName = new NamespacedNameSerializer();
class NamespaceSerializer extends Serializer_1.BaseSerializer {
    canSerialize(value) {
        return !!(typeof value === "object" && value && "namespace" in value);
    }
    serializeWithContext(value, context) {
        return context.getPrefixForNamespace(value);
    }
    deserializeWithContext(value, context) {
        if (typeof value !== "string") {
            return result_1.deserializationError({ message: "must be of type string" });
        }
        const ns = context.lookupNamespace(value);
        return result_1.deserializationValue(ns);
    }
    getType(typeSystem) {
        return new types_1.StringType();
    }
}
exports.NamespaceSerializer = NamespaceSerializer;
exports.sNamespace = new NamespaceSerializer();
//# sourceMappingURL=simpleSerializers.js.map